import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressLoyaltyProgramLoyaltyProgramsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/loyalty-programs',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/loyalty-programs',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/loyalty-programs',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/loyalty-programs',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/loyalty-programs',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/loyalty-programs',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/loyalty-programs',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/loyalty-programs',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/loyalty-programs',
        destPath: '',
      },
    ],
    '*.pub.wix-code.com': [
      {
        srcPath: '/_api/loyalty-programs',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/loyalty-programs',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_loyalty_programs';

/** Retrieves the loyalty program. */
export function getLoyaltyProgram(payload: object): RequestOptionsFactory<any> {
  function __getLoyaltyProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.program',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.program.LoyaltyPrograms.GetLoyaltyProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyProgramLoyaltyProgramsUrl({
        protoPath: '/v1/program',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyProgram.createdDate' },
              { path: 'loyaltyProgram.updatedDate' },
              { path: 'loyaltyProgram.pointDefinition.icon.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.x' },
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getLoyaltyProgram;
}

/**
 * Retrieves loyalty programs for all metasites that the caller is a member of.
 *
 * Must be called with user identity.
 */
export function bulkGetLoyaltyProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkGetLoyaltyProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.program',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.program.LoyaltyPrograms.BulkGetLoyaltyProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyProgramLoyaltyProgramsUrl({
        protoPath: '/v1/bulk/programs',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'programInSites.loyaltyProgram.createdDate' },
              { path: 'programInSites.loyaltyProgram.updatedDate' },
              {
                path: 'programInSites.loyaltyProgram.pointDefinition.icon.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'programInSites.loyaltyProgram.pointDefinition.icon.focalPoint.x',
              },
              {
                path: 'programInSites.loyaltyProgram.pointDefinition.icon.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkGetLoyaltyProgram;
}

/**
 * Updates a site's loyalty program.
 *
 * This method updates the name of the loyalty program and the details of the collectible points unit.
 * To activate the loyalty program, call Activate Loyalty Program.
 */
export function updateLoyaltyProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __updateLoyaltyProgram({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'loyaltyProgram.createdDate' },
          { path: 'loyaltyProgram.updatedDate' },
          { path: 'loyaltyProgram.pointDefinition.icon.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.x' },
          { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.program',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.loyalty.program.LoyaltyPrograms.UpdateLoyaltyProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyProgramLoyaltyProgramsUrl({
        protoPath: '/v1/program',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyProgram.createdDate' },
              { path: 'loyaltyProgram.updatedDate' },
              { path: 'loyaltyProgram.pointDefinition.icon.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.x' },
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateLoyaltyProgram;
}

/**
 * Activates a loyalty program.
 *
 * Initially, when a loyalty program is installed, the status is set to `"DRAFT"`.
 * You can change the program's status to `"ACTIVE"` with this method or through the [dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Floyalty-accounts/wizard/).
 * A site's customers can only earn or redeem points while the program is `"ACTIVE"`.
 *
 * This method only updates the status of a loyalty program. To make other updates to the program, call Update Loyalty Program.
 *
 * To temporarily pause your loyalty program, call Pause Loyalty Program and see [Pausing Your Loyalty Program](https://support.wix.com/en/article/wix-loyalty-program-pausing-your-loyalty-program) for more information.
 */
export function activateLoyaltyProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __activateLoyaltyProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.program',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.program.LoyaltyPrograms.ActivateLoyaltyProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyProgramLoyaltyProgramsUrl({
        protoPath: '/v1/program/activate',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyProgram.createdDate' },
              { path: 'loyaltyProgram.updatedDate' },
              { path: 'loyaltyProgram.pointDefinition.icon.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.x' },
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __activateLoyaltyProgram;
}

/**
 * Changes the program status to `"PAUSED"`.
 *
 * See [Pausing Your Loyalty Program](https://support.wix.com/en/article/wix-loyalty-program-pausing-your-loyalty-program) for more information.
 */
export function pauseLoyaltyProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __pauseLoyaltyProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.program',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.program.LoyaltyPrograms.PauseLoyaltyProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyProgramLoyaltyProgramsUrl({
        protoPath: '/v1/program/pause',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyProgram.createdDate' },
              { path: 'loyaltyProgram.updatedDate' },
              { path: 'loyaltyProgram.pointDefinition.icon.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.x' },
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __pauseLoyaltyProgram;
}

/** Retrieves information about the available loyalty program premium features. */
export function getLoyaltyProgramPremiumFeatures(
  payload: object
): RequestOptionsFactory<any> {
  function __getLoyaltyProgramPremiumFeatures({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.program',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.program.LoyaltyPrograms.GetLoyaltyProgramPremiumFeatures',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyProgramLoyaltyProgramsUrl({
        protoPath: '/v1/program/premium-features',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getLoyaltyProgramPremiumFeatures;
}

/** Updates the `pointsExpiration` status to `"ENABLED"`. */
export function enablePointsExpiration(
  payload: object
): RequestOptionsFactory<any> {
  function __enablePointsExpiration({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.program',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.program.LoyaltyPrograms.EnablePointsExpiration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyProgramLoyaltyProgramsUrl({
        protoPath: '/v1/program/points-expiration/enable',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyProgram.createdDate' },
              { path: 'loyaltyProgram.updatedDate' },
              { path: 'loyaltyProgram.pointDefinition.icon.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.x' },
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __enablePointsExpiration;
}

/** Updates the `pointsExpiration` status to `"DISABLED"`. */
export function disablePointsExpiration(
  payload: object
): RequestOptionsFactory<any> {
  function __disablePointsExpiration({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.program',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.program.LoyaltyPrograms.DisablePointsExpiration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyProgramLoyaltyProgramsUrl({
        protoPath: '/v1/program/points-expiration/disable',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyProgram.createdDate' },
              { path: 'loyaltyProgram.updatedDate' },
              { path: 'loyaltyProgram.pointDefinition.icon.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.x' },
              { path: 'loyaltyProgram.pointDefinition.icon.focalPoint.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __disablePointsExpiration;
}
